import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, Star, TrendingUp } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { cvList } from "../../data/content";
import { trackCvDownload } from "./CVMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const featured = cvList.find((c) => c.recommended) || cvList[0];
const others = cvList.filter((c) => c !== featured);

const TopPill = ({ testid }) => (
  <motion.span
    initial={{ opacity: 0, scale: 0.7, y: -4 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1], delay: 0.15 }}
    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2.5 py-1"
    data-testid={testid}
  >
    <motion.span
      initial={{ scale: 1 }}
      animate={{ scale: [1, 1.25, 1] }}
      transition={{ duration: 0.6, delay: 0.55, ease: "easeInOut" }}
      className="inline-flex"
    >
      <TrendingUp size={10} />
    </motion.span>
    Le plus téléchargé
  </motion.span>
);

const CvLink = ({ cv, testid }) => (
  <a
    href={cv.file}
    download={cv.download}
    onClick={() => trackCvDownload(cv.slug)}
    className="inline-flex items-center gap-2 text-sm font-medium text-cyan hover:text-white transition-colors duration-300"
    data-testid={testid}
  >
    <Download size={15} /> Télécharger ce CV
  </a>
);

export const Axes = () => {
  const [topSlug, setTopSlug] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/cv-stats/top`)
      .then(({ data }) => { if (alive && data?.top) setTopSlug(data.top.slug); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <section id="axes" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeader kicker="Cinq axes d'exploration" title="Un profil," italic="cinq axes d'exploration." />
        <Reveal>
          <p className="-mt-8 md:-mt-16 mb-14 text-base md:text-lg text-[var(--text-2)] max-w-2xl">
            {"Sélectionnez l'univers le plus proche de vos enjeux pour découvrir la version correspondante de mon CV."}
          </p>
        </Reveal>

        {/* Axe principal — Cœur de profil */}
        <Reveal>
          <motion.div
            whileHover={{ y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="group relative overflow-hidden rounded-3xl border border-cyan/30 bg-surface p-8 md:p-10 mb-4"
            data-testid="axe-card-featured"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,229,255,0.10),transparent_55%)]" />
            <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="max-w-2xl">
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-cyan bg-cyan/10 border border-cyan/30 rounded-full px-3 py-1" data-testid="axe-featured-badge">
                    <Star size={10} className="fill-cyan" /> {featured.badge || "Cœur de profil"}
                  </span>
                  {topSlug === featured.slug && <TopPill testid="axe-top-pill-featured" />}
                </div>
                <h3 className="font-display font-medium text-2xl md:text-3xl leading-snug">{featured.axe}</h3>
                <p className="text-sm md:text-base text-[var(--text-2)] leading-relaxed mt-4">{featured.desc}</p>
                <div className="flex flex-wrap gap-2 mt-6" data-testid="axe-featured-skills">
                  {featured.skills.map((s) => (
                    <span key={s} className="text-[11px] uppercase tracking-[0.12em] text-white/75 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">{s}</span>
                  ))}
                </div>
              </div>
              <div className="shrink-0">
                <CvLink cv={featured} testid="axe-featured-download" />
              </div>
            </div>
          </motion.div>
        </Reveal>

        {/* Autres axes */}
        <div className="grid sm:grid-cols-2 gap-4">
          {others.map((cv, i) => (
            <Reveal key={cv.slug} delay={(i % 2) * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group h-full flex flex-col bg-surface border border-white/5 rounded-2xl p-7 hover:border-cyan/30 transition-colors duration-400 relative overflow-hidden"
                data-testid={`axe-card-${cv.slug}`}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_top_right,rgba(0,229,255,0.06),transparent_60%)]" />
                <div className="relative flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display text-cyan/70 text-sm">{String(i + 2).padStart(2, "0")}</span>
                    {topSlug === cv.slug && <TopPill testid={`axe-top-pill-${cv.slug}`} />}
                  </div>
                  <h3 className="font-display font-medium text-xl mt-4 mb-3 leading-snug">{cv.axe}</h3>
                  <p className="text-sm text-[var(--text-2)] leading-relaxed">{cv.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4" data-testid={`axe-skills-${cv.slug}`}>
                    {cv.skills.map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-[0.1em] text-white/70 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">{s}</span>
                    ))}
                  </div>
                </div>
                <div className="relative mt-6 pt-5 border-t border-white/5">
                  <CvLink cv={cv} testid={`axe-download-${cv.slug}`} />
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

